// keys.ts — texture-key indirection + the single source of truth for the M0 palette.
//
// At M0 every "texture" is procedural (Phaser Graphics/Text/Arc/Triangle); there are no image
// files. This module exists so M2 can swap in real art by editing ONE file: register a real
// loaded-texture key via setTex(logicalKey, realKey) and the scenes that read tex(logicalKey)
// pick it up without touching draw code.

// M2 ART: fingerprinted URL imports (Vite ?url). Vite emits each file into the build; the loader
// (loadArtInto) and registry (ART_URL / ART_PAIRS) below reference them. assets/ is owned by the
// asset-production agents; the render layer only references the accepted finals.
import playerIdleUrl from '../../assets/muncher/player-idle.png?url';
import playerChompUrl from '../../assets/muncher/player-chomp-wide.png?url';
import playerMouthClosedUrl from '../../assets/muncher/player-mouth-closed.png?url';
import playerHurtUrl from '../../assets/muncher/player-hurt.png?url';
import playerWinUrl from '../../assets/muncher/player-win.png?url';
import aiIdleUrl from '../../assets/muncher/ai-idle.png?url';
import aiChompUrl from '../../assets/muncher/ai-chomp.png?url';
// P2.5 note: the P2.3 static cosmetic-skin PNGs (assets/cosmetics/*) are RETIRED from runtime —
// they predate the current muncher identity and skins now apply as tints over the animated sheet
// (see src/render/cosmetics.ts). Files + manifest rows stay as accepted source material.
import hazardHunterUrl from '../../assets/hazards/hunter.png?url';
import hazardCrawlerUrl from '../../assets/hazards/crawler.png?url';
import hazardLiarUrl from '../../assets/hazards/liar.png?url';
import hazardScramblerUrl from '../../assets/hazards/scrambler.png?url';
import tileNeutralUrl from '../../assets/tiles/tile-neutral.png?url';
import tileEatenCorrectUrl from '../../assets/tiles/tile-eaten-correct.png?url';
import tileEatenDecoyUrl from '../../assets/tiles/tile-eaten-decoy.png?url';
import tileArmedUrl from '../../assets/tiles/tile-armed-locked.png?url';
import tileScannedUrl from '../../assets/tiles/tile-scanned.png?url';
import uiButtonUrl from '../../assets/ui/ui-button.png?url';
import uiPanelUrl from '../../assets/ui/ui-panel.png?url';
import uiScanGlyphUrl from '../../assets/ui/ui-scan-glyph.png?url';
import uiLifeHeartUrl from '../../assets/ui/ui-life-heart.png?url';
import uiComboMeterUrl from '../../assets/ui/ui-combo-meter.png?url';
import uiTimerRingUrl from '../../assets/ui/ui-timer-ring.png?url';
import vfxMunchUrl from '../../assets/vfx/vfx-munch-pop.png?url';
import vfxBoardClearUrl from '../../assets/vfx/vfx-board-clear.png?url';
import vfxScanRippleUrl from '../../assets/vfx/vfx-scan-ripple.png?url';
import vfxComboBurstUrl from '../../assets/vfx/vfx-combo-burst.png?url';
import vfxWrongBuzzUrl from '../../assets/vfx/vfx-wrong-buzz.png?url';
import titleUrl from '../../assets/icon/title-mark.png?url';
import iconUrl from '../../assets/icon/app-icon.png?url';
// Animated muncher spritesheets (AutoSprite, 2026-06-30) — a SEPARATE lane from the single-image
// ART_URL map below: each is a uniform 128px frame grid sliced by Phaser's spritesheet loader and
// driven by anims (see MUNCHER_ANIMS / createMuncherAnims). The geometric muncher (muncher.ts) stays
// the headless / decode-failure fallback, so this lane is purely additive.
//
// Only the ON-MODEL sheets ship: the player + rival IDLE loops (which stay a clean cyan dot-eye wedge
// / magenta arrowhead) and the rival ADVANCE dart. The player CHOMP is DERIVED from the idle texture
// played fast (a bite with no extra art); HURT/WIN are code tint+pop reactions (reactMuncher) since
// AutoSprite's high-motion reaction frames drifted off-model (round/toothy) even at pro tier.
// Rival persona emote portraits (2026-07-02, GPT-Image edit lane FROM the accepted ai-idle
// reference — set cohesion by construction). Consumed by src/render/rivalPersona.ts.
import rivalEmoteThinkingUrl from '../../assets/muncher/emotes/rival-emote-thinking.png?url';
import rivalEmoteGloatUrl from '../../assets/muncher/emotes/rival-emote-gloat.png?url';
import rivalEmotePanicUrl from '../../assets/muncher/emotes/rival-emote-panic.png?url';
import rivalEmoteEmbarrassedUrl from '../../assets/muncher/emotes/rival-emote-embarrassed.png?url';
import msPlayerIdleUrl from '../../assets/muncher-anim/player-idle.png?url';
import msRivalIdleUrl from '../../assets/muncher-anim/rival-idle.png?url';
import msRivalAdvanceUrl from '../../assets/muncher-anim/rival-advance.png?url';
// S6 ART IDENTITY (FACE&FURY II, 2026-07-04): the rival's BOARD TOKEN pose set — the director-ratified
// c1 shard-arrow BYT3 creature (idle/taunt/chomp/defeat). Gem-locked registration (gem at 0.5,0.6333
// of every 256px frame) so the token never jumps between states. Kills the flat magenta triangle board
// token (unitsView.makeAiSprite). Consumed by src/render/game/unitsView.ts.
import rivalPoseIdleUrl from '../../assets/muncher/rival-pose-idle.png?url';
import rivalPoseTauntUrl from '../../assets/muncher/rival-pose-taunt.png?url';
import rivalPoseChompUrl from '../../assets/muncher/rival-pose-chomp.png?url';
import rivalPoseDefeatUrl from '../../assets/muncher/rival-pose-defeat.png?url';
// S6 TOPIC WORLDS: per-bucket ambient backdrop layers (portrait + landscape), painted UNDER the
// legibility scrim BEHIND the board grid (boardChrome.paintBoardWorld). Consumed via tex()/hasTex().
import worldGeographyPUrl from '../../assets/worlds/geography-portrait.webp?url';
import worldGeographyLUrl from '../../assets/worlds/geography-landscape.webp?url';
import worldMusicPUrl from '../../assets/worlds/music-portrait.webp?url';
import worldMusicLUrl from '../../assets/worlds/music-landscape.webp?url';
import worldSciencePUrl from '../../assets/worlds/science-portrait.webp?url';
import worldScienceLUrl from '../../assets/worlds/science-landscape.webp?url';
import worldSportsPUrl from '../../assets/worlds/sports-portrait.webp?url';
import worldSportsLUrl from '../../assets/worlds/sports-landscape.webp?url';
import worldArcadePUrl from '../../assets/worlds/arcade-portrait.webp?url';
import worldArcadeLUrl from '../../assets/worlds/arcade-landscape.webp?url';

/**
 * Tactical OLED Arcade palette (style-bible v2, 2026-06-28). All consumers import from here so a
 * re-skin is one edit; the handoff tokens (references/redesign/handoff/tokens.{css,json}) map 1:1
 * onto this object. Gameplay accents (player/rival/correct/hazard/armed/scan) are LOCKED and
 * colorblind-safe. Legacy names (surfaceBorder/tileBorder/banner/panel/...) are kept as aliases so
 * existing scene call-sites stay valid while new kit code uses the token names.
 */
export const PALETTE = {
  // — Surfaces: elevation ladder (dark → raised) —
  void: 0x0a0c14, // vignette / behind elevated content / modal veil
  bg: 0x11141f, // app field
  surface: 0x161b29, // panels / cards (was 0x0f1726)
  surface2: 0x1b2233, // raised / modal body
  tile: 0x202b3a, // board cells / inputs
  tileHot: 0x2c3a4f, // hover / occupied cell
  line: 0x2a3142, // hairline border
  lineStrong: 0x40546f, // visible border / cell stroke
  // — Surface aliases (legacy names → ladder) —
  surfaceBorder: 0x40546f, // = lineStrong
  tileBorder: 0x40546f, // cell stroke = lineStrong (was 0x5a718a)
  banner: 0x161b29, // rule banner = surface
  panel: 0x161b29, // modal / panel body = surface
  panelBorder: 0x2a3142, // = line
  wall: 0x0c0e16, // wall / blocked cells (near-void)
  // — Gameplay accents (LOCKED, colorblind-safe; never repurpose) —
  correct: 0x27c281, // CORRECT accent (revealed only on action — never pre-reveal)
  decoy: 0x9aa4b8, // neutral slate label on a tile (decoys look identical until acted on)
  hazard: 0xff4d6d, // danger ONLY (glitches + wrong buzz); never dilute this signal
  player: 0x22d3ee, // human muncher (cyan circle)
  ai: 0xe879f9, // AI muncher (magenta triangle)
  rival: 0xe879f9, // token alias for `ai`
  armed: 0xf59e0b, // armed-lockout amber (flickers)
  scan: 0x38bdf8, // scan information verb (magnifier tag + reveal pulse)
  gold: 0xffc53d, // NEW — win / rank-up / premium ONLY (never a gameplay state)
  // — Text ramp —
  text: 0xe5e7eb, // primary
  textDim: 0x8b93a7, // secondary
  dim: 0x8b93a7, // token alias for textDim
  mute: 0x5c6373, // NEW — tertiary / disabled
  ink: 0x0a0c14, // NEW — text on accent fills
  // — Misc / legacy —
  good: 0x27c281,
  bad: 0xff4d6d,
  white: 0xffffff,
  // Locked/blocked armed cell: a muted, "do-not-enter" steel — deliberately NOT amber, so amber
  // can be reserved for the positive "threaded gauntlet" glow moment.
  lockFill: 0x232838,
  lockBorder: 0x4a5468,
  // Muted steel for the 🔒 lock glyph + "locked" label. Bumped from 0x6b7488 → 0x9099ad at M4 so it
  // clears WCAG-AA normal-text (4.5:1) on every board background (5.67 on tile / 6.42 on bg / 6.01 on
  // panel) instead of only AA-large — it labels small text, so it must meet the stricter bar.
  lockGlyph: 0x9099ad,
  // Geometric-muncher strokes (redline): cyan body outline + magenta rival outline. Renderer-only;
  // not a gameplay-state color (the fills carry state — these are silhouette pop on the munchers).
  playerStroke: 0x0e7c8c,
  rivalStroke: 0xa21caf,
} as const;

export type PaletteKey = keyof typeof PALETTE;

/** Convert a 0xRRGGBB number to a CSS '#rrggbb' string (for Text fill / style). */
export function hex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

/**
 * Logical texture keys — the FULL M2 art surface. At M0/M1 these resolve to themselves and the
 * scenes fall back to procedural drawing because no real image is registered (hasTex() is false).
 * When the generated art lands, the ONLY edit needed is in this file: import the loaded image keys
 * and `setTex(logicalKey, realKey)` (see registerArt() below) — every scene that asks via
 * tex()/hasTex() picks it up with no draw-code change.
 *
 * Buckets:
 *   sprites      — muncher (player/AI), hazard, particle
 *   cell states  — the five distinct tile reads (neutral / eaten✓ / eaten✗ / armed-lock / scanned)
 *   ui kit       — button/panel/scan-button nine-slice-ish backgrounds
 *   vfx          — munch burst, board-clear shimmer, scan ripple
 *   brand        — title wordmark + app icon
 */
export const KEYS = {
  // sprites
  tile: 'tile',
  player: 'player',
  ai: 'ai',
  hazard: 'hazard',
  particle: 'particle',
  // muncher animation frames (player + AI) — the renderer swaps these on eat/hurt/win.
  playerIdle: 'playerIdle',
  playerChomp: 'playerChomp',
  playerMouthClosed: 'playerMouthClosed',
  playerHurt: 'playerHurt',
  playerWin: 'playerWin',
  aiIdle: 'aiIdle',
  aiChomp: 'aiChomp',
  // Rival persona emote portraits (BYT3's readout avatar — src/render/rivalPersona.ts). Neutral
  // reuses aiIdle; these four are expression edits generated FROM that reference (set cohesion).
  rivalEmoteThinking: 'rivalEmoteThinking',
  rivalEmoteGloat: 'rivalEmoteGloat',
  rivalEmotePanic: 'rivalEmotePanic',
  rivalEmoteEmbarrassed: 'rivalEmoteEmbarrassed',
  // S6 rival BOARD-TOKEN poses (BYT3 shard-arrow creature — replaces the flat magenta triangle).
  rivalPoseIdle: 'rivalPoseIdle',
  rivalPoseTaunt: 'rivalPoseTaunt',
  rivalPoseChomp: 'rivalPoseChomp',
  rivalPoseDefeat: 'rivalPoseDefeat',
  // S6 topic-world backdrops (per-bucket, portrait + landscape) — drawn under the legibility scrim.
  worldGeographyP: 'worldGeographyP',
  worldGeographyL: 'worldGeographyL',
  worldMusicP: 'worldMusicP',
  worldMusicL: 'worldMusicL',
  worldScienceP: 'worldScienceP',
  worldScienceL: 'worldScienceL',
  worldSportsP: 'worldSportsP',
  worldSportsL: 'worldSportsL',
  worldArcadeP: 'worldArcadeP',
  worldArcadeL: 'worldArcadeL',
  // hazard archetypes (Core has multiple hazard "kinds"; we vary the art per archetype)
  hazardHunter: 'hazardHunter',
  hazardCrawler: 'hazardCrawler',
  hazardLiar: 'hazardLiar',
  hazardScrambler: 'hazardScrambler',
  // cell-tile states (legibility-critical: each must read DISTINCT from the others)
  cellNeutral: 'cellNeutral',
  cellEatenCorrect: 'cellEatenCorrect',
  cellEatenDecoy: 'cellEatenDecoy',
  cellArmed: 'cellArmed',
  cellScanned: 'cellScanned',
  // UI kit
  uiButton: 'uiButton',
  uiPanel: 'uiPanel',
  uiScanButton: 'uiScanButton',
  uiLifeHeart: 'uiLifeHeart',
  uiComboMeter: 'uiComboMeter',
  uiTimerRing: 'uiTimerRing',
  // VFX
  vfxMunch: 'vfxMunch',
  vfxBoardClear: 'vfxBoardClear',
  vfxScanRipple: 'vfxScanRipple',
  vfxComboBurst: 'vfxComboBurst',
  vfxWrongBuzz: 'vfxWrongBuzz',
  // brand
  title: 'title',
  icon: 'icon',
} as const;

export type LogicalKey = keyof typeof KEYS;

const mapping: Record<string, string> = { ...KEYS };
// A logical key is only "real art" once setTex points it at a DIFFERENT (loaded image) key.
const realKeys = new Set<string>();

/** Resolve a logical key to the concrete texture key the renderer should use. */
export function tex(logical: LogicalKey): string {
  return mapping[logical] ?? logical;
}

/**
 * Has REAL generated art been registered for this logical key? Scenes branch on this:
 * true -> draw the image via tex(); false -> keep the M0/M1 procedural fallback (no behaviour
 * change until art lands, so the gate stays green with zero image files on disk).
 */
export function hasTex(logical: LogicalKey): boolean {
  return realKeys.has(logical);
}

/** M2 hook: point a logical key at a real loaded-texture key (one-file art swap). */
export function setTex(logical: LogicalKey, realKey: string): void {
  mapping[logical] = realKey;
  realKeys.add(logical);
}

/**
 * SINGLE SWAP POINT for the generated art set. When the muncher/hazard/tile/UI/VFX/title PNGs are
 * accepted (packet + runtime screenshot), do TWO things here and nothing elsewhere:
 *   1) preload them in BootScene (it calls loadArtInto below),
 *   2) uncomment the matching setTex() lines.
 * Until then this is intentionally empty -> hasTex() is false everywhere -> procedural M0 art shows.
 */
export function registerArt(): void {
  // Only register a logical key once its image actually decoded into the texture cache — guards the
  // headless/eyes harness and any decode failure (a missing texture must fall back to procedural,
  // never break the board). ART_LOADED is set by loadArtInto() after queueing the files; we still
  // double-check the cache at call time in BootScene before flipping hasTex on.
  for (const [logical, realKey] of ART_PAIRS) {
    setTex(logical, realKey);
  }
}

// Logical-key -> real loaded-texture-key pairs. The render-side swap point: every entry here makes
// hasTex(logical) true and points tex(logical) at the decoded image. Muncher animation frames and
// hazard archetypes are loaded as their OWN logical keys so the scene can swap frames per state.
const ART_PAIRS: ReadonlyArray<readonly [LogicalKey, string]> = [
  // munchers — `player`/`ai` resolve to the idle frame; the animation frames are addressable too.
  ['player', 'art-player-idle'],
  ['playerIdle', 'art-player-idle'],
  ['playerChomp', 'art-player-chomp'],
  ['playerMouthClosed', 'art-player-mouth-closed'],
  ['playerHurt', 'art-player-hurt'],
  ['playerWin', 'art-player-win'],
  ['ai', 'art-ai-idle'],
  ['aiIdle', 'art-ai-idle'],
  ['aiChomp', 'art-ai-chomp'],
  // rival persona emote portraits (readout avatar — rivalPersona.ts falls back to the geometric
  // triangle unless ALL four decode, so a partial set never mixes styles)
  ['rivalEmoteThinking', 'art-rival-emote-thinking'],
  ['rivalEmoteGloat', 'art-rival-emote-gloat'],
  ['rivalEmotePanic', 'art-rival-emote-panic'],
  ['rivalEmoteEmbarrassed', 'art-rival-emote-embarrassed'],
  // rival board-token poses (the pose path in unitsView gates on ALL four decoding; a partial set
  // falls back to the spritesheet/geometric triangle so a decode failure never mixes styles).
  ['rivalPoseIdle', 'art-rival-pose-idle'],
  ['rivalPoseTaunt', 'art-rival-pose-taunt'],
  ['rivalPoseChomp', 'art-rival-pose-chomp'],
  ['rivalPoseDefeat', 'art-rival-pose-defeat'],
  // topic-world backdrops
  ['worldGeographyP', 'art-world-geography-portrait'],
  ['worldGeographyL', 'art-world-geography-landscape'],
  ['worldMusicP', 'art-world-music-portrait'],
  ['worldMusicL', 'art-world-music-landscape'],
  ['worldScienceP', 'art-world-science-portrait'],
  ['worldScienceL', 'art-world-science-landscape'],
  ['worldSportsP', 'art-world-sports-portrait'],
  ['worldSportsL', 'art-world-sports-landscape'],
  ['worldArcadeP', 'art-world-arcade-portrait'],
  ['worldArcadeL', 'art-world-arcade-landscape'],
  // hazards — `hazard` resolves to hunter; each archetype has its own key.
  ['hazard', 'art-hazard-hunter'],
  ['hazardHunter', 'art-hazard-hunter'],
  ['hazardCrawler', 'art-hazard-crawler'],
  ['hazardLiar', 'art-hazard-liar'],
  ['hazardScrambler', 'art-hazard-scrambler'],
  // cell-tile states (drawn BEHIND the live tint rect so cell text stays legible)
  ['tile', 'art-cell-neutral'],
  ['cellNeutral', 'art-cell-neutral'],
  ['cellEatenCorrect', 'art-cell-eaten-correct'],
  ['cellEatenDecoy', 'art-cell-eaten-decoy'],
  ['cellArmed', 'art-cell-armed'],
  ['cellScanned', 'art-cell-scanned'],
  // UI kit
  ['uiButton', 'art-ui-button'],
  ['uiPanel', 'art-ui-panel'],
  ['uiScanButton', 'art-ui-scan-glyph'],
  ['uiLifeHeart', 'art-ui-life-heart'],
  ['uiComboMeter', 'art-ui-combo-meter'],
  ['uiTimerRing', 'art-ui-timer-ring'],
  // VFX
  ['vfxMunch', 'art-vfx-munch'],
  ['vfxBoardClear', 'art-vfx-board-clear'],
  ['vfxScanRipple', 'art-vfx-scan-ripple'],
  ['vfxComboBurst', 'art-vfx-combo-burst'],
  ['vfxWrongBuzz', 'art-vfx-wrong-buzz'],
  // brand
  ['title', 'art-title'],
  ['icon', 'art-icon'],
];

/**
 * Re-resolve which logical keys actually have a decoded texture in this scene's cache, and register
 * ONLY those. Called by BootScene after preload so a failed decode (or headless) falls back to the
 * procedural draw for that one key instead of pointing at a missing texture. Returns the count wired.
 */
export function registerLoadedArt(scene: import('phaser').Scene): number {
  let n = 0;
  for (const [logical, realKey] of ART_PAIRS) {
    if (scene.textures.exists(realKey)) {
      setTex(logical, realKey);
      n++;
    }
  }
  return n;
}

/**
 * Preload the generated art images. Mirrors registerArt() — add a `scene.load.image(key, url)` line
 * (URL via a Vite `?url` import, see audio.ts for the pattern) for each PNG as it is accepted. Empty
 * until art lands so BootScene's preload is a no-op and the gate is unaffected.
 */
export function loadArtInto(scene: import('phaser').Scene): void {
  for (const [realKey, url] of Object.entries(ART_URL)) {
    if (!scene.textures.exists(realKey)) {
      scene.load.image(realKey, url);
    }
  }
}

// ── Animated muncher spritesheet lane ──────────────────────────────────────────────────────────
// TEXTURES to load (the on-model sheets only) and the ANIMS built from them. The player chomp is
// derived from the idle texture (a fast bite) so no off-model chomp sheet is shipped.
export interface MuncherSheet {
  key: string;
  url: string;
  frames: number;
}
export const MUNCHER_SHEETS: MuncherSheet[] = [
  { key: 'msPlayerIdle', url: msPlayerIdleUrl, frames: 16 },
  { key: 'msRivalIdle', url: msRivalIdleUrl, frames: 12 },
  { key: 'msRivalAdvance', url: msRivalAdvanceUrl, frames: 12 },
];

interface MuncherAnim {
  anim: string;
  tex: string;
  frames: number;
  rate: number;
  repeat: number;
}
const MUNCHER_ANIMS: MuncherAnim[] = [
  { anim: 'mun-player-idle', tex: 'msPlayerIdle', frames: 16, rate: 11, repeat: -1 },
  // Chomp is the idle texture played FAST, once — a clean on-model bite with no extra art.
  { anim: 'mun-player-chomp', tex: 'msPlayerIdle', frames: 16, rate: 28, repeat: 0 },
  { anim: 'mun-rival-idle', tex: 'msRivalIdle', frames: 12, rate: 9, repeat: -1 },
  { anim: 'mun-rival-advance', tex: 'msRivalAdvance', frames: 12, rate: 20, repeat: 0 },
];

/** Queue the muncher spritesheets (128px frames). Mirrors loadArtInto; headless-safe (try/catch). */
export function loadMuncherSheetsInto(scene: import('phaser').Scene): void {
  for (const s of MUNCHER_SHEETS) {
    if (scene.textures.exists(s.key)) continue;
    try {
      scene.load.spritesheet(s.key, s.url, { frameWidth: 128, frameHeight: 128 });
    } catch {
      /* headless / no loader — geometric muncher fallback stays */
    }
  }
}

/** Create the muncher anims for every anim whose texture actually decoded. Idempotent (anims are global). */
export function createMuncherAnims(scene: import('phaser').Scene): number {
  let n = 0;
  for (const a of MUNCHER_ANIMS) {
    if (!scene.textures.exists(a.tex) || scene.anims.exists(a.anim)) continue;
    const total = Math.max(1, scene.textures.get(a.tex).frameTotal - 1); // frameTotal includes __BASE
    const end = Math.max(0, Math.min(a.frames, total) - 1);
    scene.anims.create({
      key: a.anim,
      frames: scene.anims.generateFrameNumbers(a.tex, { start: 0, end }),
      frameRate: a.rate,
      repeat: a.repeat,
    });
    n++;
  }
  return n;
}

/** True if the player idle sheet decoded — the gate the muncher uses to pick raster over geometric. */
export function muncherArtReady(scene: import('phaser').Scene): boolean {
  return scene.textures.exists('msPlayerIdle');
}

// realKey -> fingerprinted URL (Vite `?url` import). Mirrors ART_PAIRS' real keys; the loader queues
// each once. Kept here next to the registry so a new asset is a two-line edit (URL + ART_PAIRS row).
const ART_URL: Record<string, string> = {
  // munchers
  'art-player-idle': playerIdleUrl,
  'art-player-chomp': playerChompUrl,
  'art-player-mouth-closed': playerMouthClosedUrl,
  'art-player-hurt': playerHurtUrl,
  'art-player-win': playerWinUrl,
  'art-ai-idle': aiIdleUrl,
  'art-ai-chomp': aiChompUrl,
  // rival persona emotes
  'art-rival-emote-thinking': rivalEmoteThinkingUrl,
  'art-rival-emote-gloat': rivalEmoteGloatUrl,
  'art-rival-emote-panic': rivalEmotePanicUrl,
  'art-rival-emote-embarrassed': rivalEmoteEmbarrassedUrl,
  // rival board-token poses
  'art-rival-pose-idle': rivalPoseIdleUrl,
  'art-rival-pose-taunt': rivalPoseTauntUrl,
  'art-rival-pose-chomp': rivalPoseChompUrl,
  'art-rival-pose-defeat': rivalPoseDefeatUrl,
  // topic-world backdrops
  'art-world-geography-portrait': worldGeographyPUrl,
  'art-world-geography-landscape': worldGeographyLUrl,
  'art-world-music-portrait': worldMusicPUrl,
  'art-world-music-landscape': worldMusicLUrl,
  'art-world-science-portrait': worldSciencePUrl,
  'art-world-science-landscape': worldScienceLUrl,
  'art-world-sports-portrait': worldSportsPUrl,
  'art-world-sports-landscape': worldSportsLUrl,
  'art-world-arcade-portrait': worldArcadePUrl,
  'art-world-arcade-landscape': worldArcadeLUrl,
  // hazards
  'art-hazard-hunter': hazardHunterUrl,
  'art-hazard-crawler': hazardCrawlerUrl,
  'art-hazard-liar': hazardLiarUrl,
  'art-hazard-scrambler': hazardScramblerUrl,
  // tiles
  'art-cell-neutral': tileNeutralUrl,
  'art-cell-eaten-correct': tileEatenCorrectUrl,
  'art-cell-eaten-decoy': tileEatenDecoyUrl,
  'art-cell-armed': tileArmedUrl,
  'art-cell-scanned': tileScannedUrl,
  // UI
  'art-ui-button': uiButtonUrl,
  'art-ui-panel': uiPanelUrl,
  'art-ui-scan-glyph': uiScanGlyphUrl,
  'art-ui-life-heart': uiLifeHeartUrl,
  'art-ui-combo-meter': uiComboMeterUrl,
  'art-ui-timer-ring': uiTimerRingUrl,
  // VFX
  'art-vfx-munch': vfxMunchUrl,
  'art-vfx-board-clear': vfxBoardClearUrl,
  'art-vfx-scan-ripple': vfxScanRippleUrl,
  'art-vfx-combo-burst': vfxComboBurstUrl,
  'art-vfx-wrong-buzz': vfxWrongBuzzUrl,
  // brand
  'art-title': titleUrl,
  'art-icon': iconUrl,
};

/** Typography stack — the INTENTIONAL TWO-FACE SYSTEM (style-bible v2, FACE&FURY S8 #25). Both faces
 *  are bundled OFL and loaded by src/render/fonts.ts via the FontFace API (so they cover BOTH Phaser
 *  canvas text AND DOM overlays like the topic input) before the first paint:
 *    • `display` — **Chakra Petch** (squared, tactical, tabular numerals): the DISPLAY / HERO half —
 *      titles, scores, HUD numerics, hero wordmarks, buttons.
 *    • `family` / `content` — **Fredoka** (rounded, warm, humanist): the CONTENT / READING half —
 *      board answer tiles, body copy, list rows, hints, the DOM topic input. This REPLACES the old
 *      accidental `system-ui` primary (#25/#28) so the all-game reading surface finally has a voice;
 *      `system-ui` now only survives as the graceful fallback if Fredoka fails to load.
 *  `family` is kept as the back-compat alias for the existing call sites; new code may read the
 *  self-documenting `content`. Font governance (OFL + MANIFEST + legal.jsonl) landed in the same
 *  change (#45) — no Fredoka pixels ship without its licence. */
const CONTENT_FACE = '"Fredoka", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
export const FONT = {
  family: CONTENT_FACE,
  content: CONTENT_FACE,
  display: '"Chakra Petch", "Fredoka", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
} as const;
